import React, { useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { DataJoinService } from '../services/dataJoins';
import { CalculationService } from '../services/calculationService';
import { Transaction } from '../types';
import { SavedAnalysis } from '../types/analytics';
import { formatDateForAnalysis, compareFormattedDates } from '../lib/utils';

const COLORS = ['#E8AFB6', '#AFE8C5', '#AFD3E8', '#FFD97D', '#FF6B6B', '#95E1D3'];

interface AnalysisChartRendererProps {
  analysis: SavedAnalysis;
  transactions: Transaction[];
  gradingOrders: any[];
  height?: number;
}

// Helper functions
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};


const aggregateMetric = (items: any[], metric: string): number => {
  // Handle count metrics with specific logic
  if (metric === 'count_purchased') {
    return items.length;
  }
  if (metric === 'count_sold') {
    return items.filter(item => item.sale_date && item.sale_date.toString().trim() !== '').length;
  }
  if (metric === 'count_graded') {
    return items.filter(item => item.grading_order_id && item.grading_order_id.toString().trim() !== '').length;
  }
  
  // Handle calculated metrics
  if (metric === 'total_fees') {
    return items.reduce((sum, item) => {
      const marketplaceFees = typeof item.marketplace_fees === 'number' ? item.marketplace_fees : parseFloat(item.marketplace_fees || '0');
      const shippingFees = typeof item.shipping_fees === 'number' ? item.shipping_fees : parseFloat(item.shipping_fees || '0');
      return sum + marketplaceFees + shippingFees;
    }, 0);
  }
  
  if (metric === 'total_gems') {
    const { PreferencesService } = require('../services/preferencesService');
    return items.filter(item => {
      if (!item.grade || !item.grading_order_id) return false;
      
      // Parse grade string (e.g., "PSA 10" -> company: "PSA", grade: "10")
      const companies = ['PSA', 'BGS', 'SGC', 'CSG', 'CGC', 'HGA', 'GMA', 'ISA'];
      let company = '';
      let gradeValue = '';
      
      for (const comp of companies) {
        if (item.grade.toUpperCase().includes(comp)) {
          company = comp;
          const regex = new RegExp(`${comp}\\s*(\\d+(?:\\.\\d+)?)`, 'i');
          const match = item.grade.match(regex);
          if (match) {
            gradeValue = match[1];
          }
          break;
        }
      }
      
      if (!company || !gradeValue) return false;
      return PreferencesService.isGemGrade(company, gradeValue);
    }).length;
  }
  
  if (metric === 'gem_rate') {
    const totalGraded = items.filter(item => item.grading_order_id && item.grading_order_id.toString().trim() !== '').length;
    if (totalGraded === 0) return 0;
    
    const totalGems = aggregateMetric(items, 'total_gems');
    return (totalGems / totalGraded) * 100;
  }

  // Default: sum numeric values
  return items.reduce((sum, item: any) => {
    const value = item[metric];
    if (typeof value === 'number') return sum + value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value.replace(/[$,]/g, ''));
      return sum + (isNaN(parsed) ? 0 : parsed);
    }
    return sum;
  }, 0);
};

const formatValue = (value: number, metric: string, availableMetrics: any[]): string => {
  const metricOption = availableMetrics.find(m => m.value === metric);
  if (!metricOption) return value.toString();

  if (metricOption.type === 'currency') {
    return `$${value.toFixed(2)}`;
  }
  if (metricOption.type === 'percentage') {
    return `${value.toFixed(2)}%`;
  }
  return value.toLocaleString();
};

const formatMetricLabel = (metric: string, availableMetrics: any[]): string => {
  return availableMetrics.find(m => m.value === metric)?.label || metric;
};

const AnalysisChartRenderer: React.FC<AnalysisChartRendererProps> = ({
  analysis,
  transactions,
  gradingOrders,
  height = 300
}) => {
  const availableMetrics = useMemo(() => [
    { value: 'cost', label: 'Purchase Cost', type: 'currency' },
    { value: 'sale_price', label: 'Sale Price', type: 'currency' },
    { value: 'calculated_total_cost', label: 'Total Cost', type: 'currency' },
    { value: 'calculated_net_profit', label: 'Net Profit', type: 'currency' },
    { value: 'grading_cost', label: 'Grading Cost', type: 'currency' },
    { value: 'marketplace_fees', label: 'Marketplace Fees', type: 'currency' },
    { value: 'shipping_fees', label: 'Shipping Fees', type: 'currency' },
    { value: 'total_fees', label: 'Total Fees', type: 'currency' },
    { value: 'roi', label: 'ROI', type: 'percentage' },
    { value: 'count_purchased', label: 'Count Purchased', type: 'number' },
    { value: 'count_sold', label: 'Count Sold', type: 'number' },
    { value: 'count_graded', label: 'Count Graded', type: 'number' },
    { value: 'total_gems', label: 'Total Gems', type: 'number' },
    { value: 'gem_rate', label: 'Gem Rate', type: 'percentage' },
  ], []);

  // Process data similar to AnalysisBuilder
  const processedData = useMemo(() => {
    if (transactions.length === 0) return [];

    // Join transactions with grading orders
    let joinedData = DataJoinService.joinTransactionsWithGradingOrders(transactions, gradingOrders);

    // Apply calculations
    joinedData = CalculationService.enrichTransactions(joinedData, gradingOrders);

    // Apply filters from analysis
    let filtered = joinedData;
    const filters = analysis.filters || {};

    // Apply categorical filters
    if (filters.categorical) {
      Object.entries(filters.categorical).forEach(([field, values]: [string, any]) => {
        if (Array.isArray(values) && values.length > 0) {
          filtered = filtered.filter((item: any) => values.includes(item[field]));
        }
      });
    }

    // Apply numeric filters (check for _min/_max suffix pattern from AnalysisBuilder serialization)
    Object.entries(filters).forEach(([key, value]: [string, any]) => {
      if (key.endsWith('_min') || key.endsWith('_max')) {
        const field = key.replace(/_min$|_max$/, '');
        const isMin = key.endsWith('_min');
        filtered = filtered.filter((item: any) => {
          const itemValue = typeof item[field] === 'number' ? item[field] : parseFloat(item[field] || '0');
          if (isMin) {
            return itemValue >= value;
          } else {
            return itemValue <= value;
          }
        });
      }
    });

    // Apply date range filters (check for _start/_end suffix pattern)
    Object.entries(filters).forEach(([key, value]: [string, any]) => {
      if (key.endsWith('_start') || key.endsWith('_end')) {
        const field = key.replace(/_start$|_end$/, '');
        const isStart = key.endsWith('_start');
        if (value) {
          const filterDate = parseDate(value);
          if (filterDate) {
            filtered = filtered.filter((item: any) => {
              const itemDate = parseDate(item[field]);
              if (!itemDate) return false;
              if (isStart) {
                return itemDate >= filterDate;
              } else {
                return itemDate <= filterDate;
              }
            });
          }
        }
      }
    });
    
    // Smart filtering based on selected metrics
    const metrics = analysis.metrics || [];
    
    // If analyzing sales metrics, exclude items that haven't been sold
    const salesMetrics = ['sale_price', 'calculated_net_profit', 'roi', 'count_sold'];
    const hasSalesMetric = metrics.some(m => salesMetrics.includes(m));
    if (hasSalesMetric) {
      filtered = filtered.filter(item => item.sale_date && item.sale_date.toString().trim() !== '');
    }
    
    // If analyzing grading metrics, exclude items that haven't been graded
    const gradingMetrics = ['grading_cost', 'total_gems', 'gem_rate', 'count_graded'];
    const hasGradingMetric = metrics.some(m => gradingMetrics.includes(m));
    if (hasGradingMetric) {
      filtered = filtered.filter(item => item.grading_order_id && item.grading_order_id.toString().trim() !== '');
    }

    return filtered;
  }, [analysis, transactions, gradingOrders]);

  // Determine chart type from analysis (respect saved chartType if valid, otherwise auto-detect)
  const chartType = useMemo(() => {
    const metrics = analysis.metrics || [];
    const categoricals = analysis.groupBy || [];
    
    // Check if there's a time dimension - either via timeGranularity or a _grouped date field in groupBy
    const hasGroupedDateField = categoricals.some(cat => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const hasTrend = (analysis.timeGranularity && analysis.timeGranularity !== 'none') || hasGroupedDateField;
    
    // Separate time dimensions from categorical dimensions
    const timeDimensions = categoricals.filter(cat => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const nonTimeDimensions = categoricals.filter(cat => !timeDimensions.includes(cat));

    // Helper to determine valid chart types for this data shape
    const getValidChartTypes = (): string[] => {
      const validTypes: string[] = [];
      
      if (metrics.length === 0) return ['blank'];
      if (metrics.length === 1 && !hasTrend && categoricals.length === 0) return ['scalar'];
      if (metrics.length === 1 && hasTrend && nonTimeDimensions.length === 0) {
        validTypes.push('line', 'bar');
        return validTypes;
      }
      if (metrics.length === 1 && !hasTrend && nonTimeDimensions.length === 1) {
        validTypes.push('bar', 'pie');
        return validTypes;
      }
      if (metrics.length === 1 && !hasTrend && nonTimeDimensions.length === 2) {
        validTypes.push('stacked_bar');
        return validTypes;
      }
      if (metrics.length === 1 && hasTrend && nonTimeDimensions.length > 0) {
        validTypes.push('stacked_bar', 'multiple_line');
        return validTypes;
      }
      if (metrics.length > 1 && hasTrend && nonTimeDimensions.length === 0) {
        validTypes.push('multiple_line', 'stacked_bar');
        return validTypes;
      }
      if (metrics.length > 1 && !hasTrend && nonTimeDimensions.length > 0) {
        validTypes.push('bar');
        return validTypes;
      }
      validTypes.push('bar');
      return validTypes;
    };

    // Check if saved chartType is valid for this data shape
    const validTypes = getValidChartTypes();
    if (analysis.chartType && validTypes.includes(analysis.chartType)) {
      return analysis.chartType;
    }

    // Fall back to auto-detection (first valid type)
    return validTypes[0];
  }, [analysis]);

  // Process chart data
  const chartData = useMemo(() => {
    if (processedData.length === 0) return [];

    const metrics = analysis.metrics || [];
    const categoricals = analysis.groupBy || [];
    
    // Check if there's a time dimension
    const groupedDateField = categoricals.find(cat => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const hasTrend = (analysis.timeGranularity && analysis.timeGranularity !== 'none') || !!groupedDateField;
    const granularity = analysis.timeGranularity || 'month';

    // Extract trend field from groupBy (look for _grouped suffix) or infer from metrics
    let trendField = 'purchase_date';
    if (groupedDateField) {
      trendField = groupedDateField.replace('_grouped', '');
    } else {
      // Infer from metrics if not in groupBy (for backward compatibility)
      const saleMetrics = ['sale_price', 'calculated_net_profit', 'roi', 'count_sold'];
      const purchaseMetrics = ['cost', 'calculated_total_cost', 'count_purchased'];
      if (metrics.some(m => saleMetrics.includes(m))) {
        trendField = 'sale_date';
      } else if (metrics.some(m => purchaseMetrics.includes(m))) {
        trendField = 'purchase_date';
      }
    }

    // Filter out items without the relevant date field when doing time-based analysis
    let dataForChart = processedData;
    if (hasTrend) {
      dataForChart = dataForChart.filter((item: any) => {
        const dateStr = item[trendField];
        return dateStr && dateStr.toString().trim() !== '';
      });
    }
    
    // Add grouped date fields if trend selected and not already in groupBy
    const enrichedData = dataForChart.map((item: any) => {
      const result: any = { ...item };
      if (hasTrend && granularity && granularity !== 'none') {
        // If grouped field already exists in data, use it; otherwise create it
        const groupedFieldName = `${trendField}_grouped`;
        if (!result[groupedFieldName]) {
          const date = parseDate(item[trendField]);
          if (date) {
            result[groupedFieldName] = formatDateForAnalysis(date, granularity);
          }
        }
      }
      return result;
    });

    // Separate time dimensions from categorical dimensions
    const timeDimensions = categoricals.filter(cat => 
      cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
    );
    const nonTimeDimensions = categoricals.filter(cat => !timeDimensions.includes(cat));

    const isStackedBar = chartType === 'stacked_bar';
    const isMultipleLine = chartType === 'multiple_line';

    // Stacked / multi-line-by-category only when a categorical breakdown exists (matches AnalysisBuilder).
    const useStackedGrouping =
      isStackedBar || (isMultipleLine && nonTimeDimensions.length > 0 && hasTrend);

    if (useStackedGrouping) {
      let xAxisDimension: string | null = null;
      let stackDimension: string | null = null;

      if (hasTrend) {
        const timeDim = timeDimensions.length > 0 ? timeDimensions[0] : `${trendField}_grouped`;
        xAxisDimension = timeDim;
        stackDimension = nonTimeDimensions.length > 0 ? nonTimeDimensions[0] : null;
      } else if (categoricals.length === 2) {
        xAxisDimension = categoricals[0];
        stackDimension = categoricals[1];
      }

      if (!xAxisDimension || !stackDimension) {
        return [];
      }

      const xAxisGroups: Record<string, Record<string, any[]>> = {};

      enrichedData.forEach((item: any) => {
        const xValue = (item as any)[xAxisDimension!] || 'Unknown';
        const stackValue = (item as any)[stackDimension!] || 'Unknown';

        if (!xAxisGroups[xValue]) {
          xAxisGroups[xValue] = {};
        }
        if (!xAxisGroups[xValue][stackValue]) {
          xAxisGroups[xValue][stackValue] = [];
        }
        xAxisGroups[xValue][stackValue].push(item);
      });

      const allStackValues = new Set<string>();
      Object.values(xAxisGroups).forEach(stackGroups => {
        Object.keys(stackGroups).forEach(stackValue => {
          allStackValues.add(stackValue);
        });
      });
      const sortedStackValues = Array.from(allStackValues).sort();

      const result: any[] = [];
      Object.entries(xAxisGroups)
        .sort(([a], [b]) => {
          // For date-based charts, ensure chronological sorting (oldest to newest)
          if (hasTrend) {
            // Use the proper date comparison function that handles all formats
            return compareFormattedDates(a, b);
          }
          return a.localeCompare(b);
        })
        .forEach(([xValue, stackGroups]) => {
          const record: any = { name: xValue };

          if (isStackedBar) {
            // Stacked bar: one metric, multiple stack values
            metrics.forEach(metric => {
              sortedStackValues.forEach(stackValue => {
                const items = stackGroups[stackValue] || [];
                record[stackValue] = aggregateMetric(items, metric);
              });
            });
          } else if (isMultipleLine) {
            // Multiple line: one metric, lines for each categorical value
            const metric = metrics[0]; // Multiple line with categorical uses single metric
            sortedStackValues.forEach(stackValue => {
              const items = stackGroups[stackValue] || [];
              record[stackValue] = aggregateMetric(items, metric);
            });
          }

          result.push(record);
        });

      return result;
    } else {
      // Regular grouping (non-stacked)
      const groups: Record<string, any[]> = {};
      
      // Separate time dimensions from categorical dimensions
      const timeDimensions = categoricals.filter(cat => 
        cat.endsWith('_grouped') && (cat.includes('date') || cat.includes('Date'))
      );
      const nonTimeDimensions = categoricals.filter(cat => !timeDimensions.includes(cat));
      
      enrichedData.forEach((item: any) => {
        const keys: string[] = [];

        // Add categorical dimensions
        nonTimeDimensions.forEach((cat: string) => {
          keys.push((item as any)[cat] || '');
        });

        // Add time dimension if present
        if (hasTrend) {
          if (timeDimensions.length > 0) {
            // Use the grouped date field from groupBy
            timeDimensions.forEach(timeDim => {
              keys.push((item as any)[timeDim] || 'Unknown');
            });
          } else {
            // Fallback: create grouped field
            const groupedField = `${trendField}_grouped`;
            keys.push((item as any)[groupedField] || 'Unknown');
          }
        }

        const groupKey = keys.length > 0 ? keys.join('|') : 'All';

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
      });

      const result: any[] = [];
      Object.entries(groups)
        .sort(([a], [b]) => {
          // For date-based charts, ensure chronological sorting (oldest to newest)
          if (hasTrend && timeDimensions.length > 0) {
            // Extract the time value from the key for proper sorting
            const timeParts = a.split('|');
            const timeA = timeParts[timeParts.length - 1] || a;
            const timePartsB = b.split('|');
            const timeB = timePartsB[timePartsB.length - 1] || b;
            // Use proper date comparison that handles all user-defined formats
            return compareFormattedDates(timeA, timeB);
          }
          return a.localeCompare(b);
        })
        .forEach(([key, items]) => {
          const groupParts = key.split('|');
          const record: any = {
            name: groupParts[groupParts.length - 1],
          };

          metrics.forEach(metric => {
            record[metric] = aggregateMetric(items, metric);
          });

          result.push(record);
        });

      return result;
    }
  }, [analysis, processedData, chartType]);

  const metrics = analysis.metrics || [];
  const categoricals = analysis.groupBy || [];

  if (chartType === 'blank' || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 border rounded-lg bg-muted/10">
        <p className="text-muted-foreground text-sm">No data available</p>
      </div>
    );
  }

  if (chartType === 'pie') {
    // Pie charts need special data format - use first metric as value, name as label
    const pieData = chartData.map((item: any) => ({
      name: item.name,
      value: item[metrics[0]] || 0
    }));

    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
            outerRadius={height * 0.35}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry: any, index: number) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              fontSize: '12px'
            }}
            formatter={(value: number) => {
              return formatValue(value, metrics[0], availableMetrics);
            }}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'scalar') {
    const value = chartData[0]?.[metrics[0]] || 0;
    return (
      <div className="flex items-center justify-center h-48 border rounded-lg bg-gradient-primary/10">
        <div className="text-center">
          <p className="text-3xl font-bold">{formatValue(value, metrics[0], availableMetrics)}</p>
          <p className="text-muted-foreground text-sm mt-1">{formatMetricLabel(metrics[0], availableMetrics)}</p>
        </div>
      </div>
    );
  }

  if (chartType === 'line' || chartType === 'multiple_line') {
    // For multiple_line with categorical breakdown, get line keys from chart data (categorical values)
    // For regular line or multiple_line with multiple metrics, use metrics
    let lineKeys: string[] = [];
    let isCategoricalLines = false;

    if (chartType === 'multiple_line' && chartData.length > 0) {
      // Check if chart data has categorical keys (like stacked_bar structure)
      const firstRow = chartData[0] as any;
      const categoricalKeys = Object.keys(firstRow).filter(key => {
        if (key === 'name') return false;
        // Check if this key corresponds to a categorical dimension value
        // (not a metric name that matches our metric list)
        return !metrics.includes(key);
      });

      // If we have categorical keys and only one metric, use categorical as lines
      if (categoricalKeys.length > 0 && metrics.length === 1) {
        lineKeys = categoricalKeys;
        isCategoricalLines = true;
      } else {
        // Fallback: use metrics as lines (multiple metrics, no categorical)
        lineKeys = metrics;
      }
    } else {
      // Regular line chart: use metrics
      lineKeys = metrics;
    }

    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
          <XAxis 
            dataKey="name" 
            stroke="hsl(var(--muted-foreground))" 
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis width={72} tickFormatter={(v) => formatValue(v, metrics[0], availableMetrics)} stroke="hsl(var(--muted-foreground))" fontSize={12} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              fontSize: '12px'
            }}
            formatter={(value: number, name: string) => {
              // For categorical lines, use the first metric for formatting
              // For metric lines, find the matching metric
              const metric = isCategoricalLines 
                ? metrics[0]
                : metrics.find(m => formatMetricLabel(m, availableMetrics) === name) || metrics[0];
              return formatValue(value, metric, availableMetrics);
            }}
            labelFormatter={(label) => label}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          {lineKeys.map((key, idx) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={COLORS[idx % COLORS.length]}
              name={isCategoricalLines ? key : formatMetricLabel(key, availableMetrics)}
              strokeWidth={2}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'stacked_bar') {
    // Get the stack keys (sport names, etc.) for tooltip formatting
    const stackKeys = chartData[0] ? Object.keys(chartData[0])
      .filter(key => {
        if (key === 'name') return false;
        if (categoricals.includes(key)) return false;
        return true;
      }) : [];
    
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
          <XAxis 
            dataKey="name" 
            stroke="hsl(var(--muted-foreground))" 
            fontSize={12}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis width={72} tickFormatter={(v) => formatValue(v, metrics[0], availableMetrics)} stroke="hsl(var(--muted-foreground))" fontSize={12} />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              fontSize: '12px'
            }}
            formatter={(value: number, name: string) => {
              // Stacked bar charts use one metric, format based on that metric
              const metric = metrics[0];
              return formatValue(value, metric, availableMetrics);
            }}
            labelFormatter={(label) => label}
          />
          <Legend wrapperStyle={{ fontSize: '12px' }} />
          {stackKeys.map((key, idx) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="stack"
              fill={COLORS[idx % COLORS.length]}
              name={key}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Default bar chart
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
        <XAxis 
          dataKey="name" 
          stroke="hsl(var(--muted-foreground))" 
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis width={72} tickFormatter={(v) => formatValue(v, metrics[0], availableMetrics)} stroke="hsl(var(--muted-foreground))" fontSize={12} />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--popover))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '6px',
            fontSize: '12px'
          }}
          formatter={(value: number, name: string) => {
            // Find the metric by matching the formatted label
            const metric = metrics.find(m => formatMetricLabel(m, availableMetrics) === name) || metrics[0];
            return formatValue(value, metric, availableMetrics);
          }}
          labelFormatter={(label) => label}
        />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        {metrics.map((metric, idx) => (
          <Bar
            key={metric}
            dataKey={metric}
            fill={COLORS[idx % COLORS.length]}
            name={formatMetricLabel(metric, availableMetrics)}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default AnalysisChartRenderer;
